/**
 * @title Navigation
 * @description Top navigation stepper shown on all, except the front page
 * @image https://storageaccountourimbea2.blob.core.windows.net/tradein2guide/mainpage-stepper.png
 */
import * as React from "react";
import {FC, useCallback, useEffect, useState} from "react";
import {GlobalLayout, StyledTypography} from "../../providers/layout/LayoutProvider";
import {Box, Step, StepConnector, stepConnectorClasses, StepIconProps, StepLabel, Stepper, styled} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {useRecoilValue} from "recoil";
import {ReturnsCartAtom, ReturnsCartView, ReturnsSum} from "./returns/ReturnsCart";
import {ProfilePagePath} from "./profile/ProfilePage";
import {useNavigate} from "react-router-dom";
import {DeliveryPagePath} from "./delivery/DeliveryPage";
import {PubSub} from "../../utility/PubSub";
import {AccessContextData} from "../../providers/access/AppContextProvider";
import {ReturnsPagePath} from "./returns/ReturnsPage";
import {PayoutPagePath} from "./payout/PayoutPage";
import {LocalizationsAtom} from "../../providers/localization/LocalizationProvider";
import clsx from "clsx";
import {AppDataAtom} from "../../providers/access/ApplicationDataProvider";
import {CommentsPagePath} from "./comments/CommentsPage";

type StepType = 'returns' | 'profile' | 'shipping' | 'tradein' | 'payout' | 'comments';

export type PrivatePageProps = {
   step?: StepType
   onNextPath?: () => Promise<string>
   hideSteppers?: boolean
   swirlOnSubmit?: boolean
}

type StepData = {
   name: StepType
   isActive: () => boolean
   isNextEnabled: () => Promise<boolean>
   title: () => string
   navigate: (back?: boolean) => void
   isHidden?: () => boolean
}

export const PrivateRenderer: FC<PrivatePageProps> = ({step, hideSteppers, children, onNextPath, swirlOnSubmit}) => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useStyles();
   const cart = useRecoilValue(ReturnsCartAtom)
   const appData = useRecoilValue(AppDataAtom);
   const appContext = useRecoilValue(AccessContextData);
   const [block, setBlock] = useState<boolean>(false);
   const sum = useRecoilValue(ReturnsSum);
   const [steppers, setSteppers] = useState<StepData[]>([])
   const isProfilePageHidden = !!window.useExternalCustomerData;

   const navigate = useNavigate();

   useEffect(() => {
      let step1: StepData = {
         name: 'returns',
         isActive: () => step === 'returns',
         isNextEnabled: async () => true,
         title: () => localizations.mainPageStepperReturns,
         navigate: (back?: boolean) => {
            navigate(ReturnsPagePath(), {
               replace: back
            })
         }
      };
      const stepConsumerInfo: StepData = {
         name: 'profile',
         isActive: () => step === 'profile',
         isNextEnabled: async () => {
            const formValid = await PubSub.emitSync('consumerform-valid')
            for (const formValidElement of formValid) {
               if(!formValidElement) {
                  return false
               }
            }
            return true;
         },
         title: () => localizations.mainPageStepperProfile,
         navigate: (back?: boolean) => {
            navigate(ProfilePagePath(), {
               replace: back
            })
         }
      };
      const stepPayout: StepData = {
         name: 'payout',
         isActive: () => step === 'payout',
         isNextEnabled: async () => !!cart.payoutType,
         title: () => localizations.mainPageStepperPayout,
         navigate: (back?: boolean) => {
            navigate(PayoutPagePath(), {
               replace: back
            })
         }
      };

      const stepShipping: StepData = {
         name: 'shipping',
         isActive: () => step === 'shipping',
         isNextEnabled: async () => true,
         title: () => localizations.mainPageStepperDelivery,
         navigate: (back?: boolean) => {
            navigate(DeliveryPagePath(), {
               replace: back
            })
         }
      };

      let stepComments: StepData = {
         name: 'comments',
         isActive: () => step === 'comments',
         isNextEnabled: async () => {
            const formValid = await PubSub.emitSync('commentspage-valid')
            for (const formValidElement of formValid) {
               if(!formValidElement) {
                  return false
               }
            }
            return true;
         },
         title: () => localizations.mainPageStepperComments,
         navigate: (back?: boolean) => {
            navigate(CommentsPagePath(), {
               replace: back
            })
         }
      };

      const newSteppers: StepData[] = [
         step1
      ];
      if(appContext.tradeInComments) {
         newSteppers.push(stepComments);
      }
      if (appContext.circularPoints && appContext.currency !== 'POINTS' && appContext.currency !== 'POINT') {
         newSteppers.push(stepPayout);
      } else if(appContext.tradeInPayoutEnabled) {
         newSteppers.push(stepPayout);
      }
      newSteppers.push(stepConsumerInfo);
      newSteppers.push(stepShipping);
      setSteppers(newSteppers);

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [navigate, step])

   let indexActive = steppers.findIndex(step => step.isActive());
   const currentStep = steppers[indexActive];

   const onNext = useCallback(async () => {
      if (!await currentStep?.isNextEnabled()) {
         PubSub.emit('navigation-blocked', true)
         return;
      }

      if (appData.maximumVoucher && sum > appData.maximumVoucher) {
         PubSub.emit('navigation-blocked', true)
         return;
      }

      if (onNextPath) {
         setBlock(true);
         try {
            const nextPath = await onNextPath();
            if (nextPath) {
               navigate(nextPath)
            }
         } finally {
            setBlock(false);
         }
      }
      const nextStep = steppers[indexActive + 1];

      nextStep?.navigate();
   }, [currentStep, appData.maximumVoucher, sum, onNextPath, steppers, indexActive, navigate]);

   const onPrev = useCallback(() => {
      if (indexActive === 0) {
         return;
      }

      let nextStep = steppers[indexActive - 1];
      if (isProfilePageHidden) {
         if(nextStep.name === 'profile') {
            nextStep = steppers[indexActive - 2];
         }
      }
      nextStep?.navigate(true);
   }, [indexActive, steppers, isProfilePageHidden])
   
   useEffect(() => {
      return PubSub.on('navigation-forward', async (blocked: boolean) => {
         await onNext();
      });
   }, [onNext]);

   useEffect(() => {
      return PubSub.on('navigation-backward', async (blocked: boolean) => {
         onPrev();
      });
   }, [onPrev]);


   if (steppers.length === 0) {
      return <></>
   }

   let currentStepNr = 0

   return <div className={clsx(classes.container, 'c2s-private-container')}>
      {(!hideSteppers && step !== 'tradein') && <>
         <Box className={classes.stepperContainer}>
            <Stepper alternativeLabel connector={<ColorlibConnector className={"c2s-stepper-line"}/>}
                     className={classes.stepper}>
               {steppers.map((stepper, i) => {
                  let completed = i < indexActive;
                  let isActive = i === indexActive;
                  if(stepper.isHidden && stepper.isHidden()) {
                     return null
                  }

                  if(stepper.name === 'profile' && isProfilePageHidden) {
                     return null
                  }

                  currentStepNr++

                  return <Step key={'step_' + i} active={isActive} completed={completed} onClick={() => {
                     if (i < indexActive) {
                        steppers[i].navigate()
                     }
                  }}>
                     <StepLabel style={{cursor: completed ? 'pointer' : 'inherit'}}
                                StepIconComponent={StepIconComponent} icon={"" + currentStepNr}>
                        <StyledTypography type={"main"} variant={"subtitle1"}
                                          className={clsx(isActive && classes.stepLabelActive, 'c2s-stepper-title')}
                        >
                           {stepper.title()}
                        </StyledTypography>
                     </StepLabel>
                  </Step>
               })}
            </Stepper>
         </Box>
      </>}
      <Box sx={{width: '100%', marginBottom: '100px'}}>
         {children}
      </Box>
      {!hideSteppers &&
         <ReturnsCartView onPrev={onPrev} onNext={onNext} prevVisible={indexActive !== 0}
                          nextEnabled={true}
                          swirl={block && swirlOnSubmit}/>
      }
   </div>
}

const useStyles = makeStyles({
   container: {
      width: '1000px',
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      marginBottom: '100px', // more than height of return-item-cart,
      userSelect: 'none',
      webkitUserSelect: 'none', /* Safari */
      khtmlUserSelect: 'none', /* Konqueror HTML */
      mozUserSelect: 'none', /* Firefox */
      msUserSelect: 'none' /* Internet Explorer/Edge */
   },
   stepperContainer: {
      padding: '50px'
   },
   stepper: {
      width: '700px'
   },
   stepLabelActive: {
      fontWeight: 'bold'
   },
   '@media only screen and (max-width: 1000px)': {
      container: {
         width: '100%'
      } as any,
      stepperContainer: {
         padding: '20px'
      },
   },
   '@media only screen and (max-width: 800px)': {
      container: {
         width: '100%'
      } as any,
      stepperContainer: {
         padding: '20px 5px 20px 5px'
      },
      stepper: {
         width: '100%'
      },
   },
});

const StepIconComponent: FC<StepIconProps> = ({active, completed, icon}) => {
   return <ColorlibStepIconRoot ownerState={{completed: completed, active: active}}>{icon}</ColorlibStepIconRoot>
}

const ColorlibStepIconRoot = styled('div')<{
   ownerState: { completed?: boolean; active?: boolean };
}>(({theme, ownerState}) => {
   const globalLayout = useRecoilValue(GlobalLayout);

   let color = globalLayout.text?.textColor || '#000000';
   if (globalLayout.widgets?.selectionTextColor && ownerState.active) {
      color = globalLayout.widgets?.selectionTextColor;
   }
   return {
      backgroundColor: 'inherit',
      fontWeight: 'bold',
      fontSize: 16,
      color: color,
      border: (globalLayout.widgets?.stepperBorderWidth || '3px') + ' solid #E0E0E0',
      width: '40px !important',
      height: 40,
      lineHeight: 16,
      display: 'flex',
      borderRadius: '50%',
      justifyContent: 'center',
      alignItems: 'center',
      webkitBoxSizing: 'border-box', /* Safari/Chrome, other WebKit */
      MozBoxSizing: 'border-box',    /* Firefox, other Gecko */
      boxSizing: 'border-box',
      ...(ownerState.active && {
         border: '3px solid ' + globalLayout.widgets?.selectionColor || '#9DF7E5',
         backgroundColor: globalLayout.widgets?.selectionColor || '#9DF7E5',
      }),
      ...(ownerState.completed && {
         backgroundColor: '#E0E0E0',
      }),
   }
});

const ColorlibConnector = styled(StepConnector)(({theme}) => ({
   [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 20,
   },
   [`& .${stepConnectorClasses.line}`]: {
      height: 3,
      border: 0,
      backgroundColor: '#e0e0e0',
      borderRadius: 1,
   },
}));
